import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom';
import { capitalizeFirstLetter, localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { ListView, NestedValueExtractor } from '../../general_components/content_management/list_view/listView';
import { loadStatusFormatter, loadStatusColorGenerator } from '../loads/loadDataDelegate';
import { driverTypeFormatter } from '../drivers/driverAddConf';
import { DriverStatusFormatter } from '../drivers/driverDataDelegate';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { BROKER_LISTING_CONF } from '../brokers/brokerDataDelegate';
import { canSeeLoadRates } from '../../supplementary_components/userDataDelegate';

const Wrapper = styled.div`
    &>h1{
        color:var(--dark-gray);
        
    }
    hr{
        border: none; 
        height: 1px; 
        background-color: var(--extra-light-gray);
        margin: 1rem 0 1.2rem 0;
    }
    &>.noResults{
        font-style: italic;
        color:var(--gray);
        font-size:1.2rem;
    }
    &>div{
        &>h2{
            color:var(--dark-gray);
            font-size:1.4rem;
            margin-top:3rem;
        }
    }
`
export const SearchResults = () => {
    const [data, setData] = useState(null);
    const queryParams = new URLSearchParams(useLocation().search);
    const decodedParamValue = decodeURIComponent(queryParams.get('val'));
    const userCanSeeLoadRates = canSeeLoadRates();
    
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        sendRequest('GET', API_ROOT+`search?searchPage=true&pattern=`+decodedParamValue, signal, (res)=>{
            setData(res);
        }, ()=>{
            setData(undefined);
        }, null);
        return () => {
            // cancel the request before component unmounts
            controller.abort();
        }
        // eslint-disable-next-line
    }, [])
    return <Wrapper>
        <h1>Search Results</h1>
        {decodedParamValue === "" || (data && Object.keys(data).length === 0) ?
            <div className="noResults">No Results...</div>
        :
        data === undefined ?
            <div className="noResults">Something Went Wrong</div>
        :
        data ? 
            <SearchResultsView data={data} canSeeRates={userCanSeeLoadRates} />
        :
            <LoadingIcon state={true} height={"10rem"} />
        }
        
    </Wrapper>
}
function lblToConf (lbl, canSeeRates = true){
    switch (lbl) {
        case "loads": {
            const columns = [
                {
                    label: "Load",
                    columnName: "loadNumber",
                    clickable: true,
                    identifierColumnName: "id",
                    path: '/loads/',
                },
                {
                    label: "Start Date",
                    columnName: "startDate",
                    valueInterpreter: (value)=>{
                        return localDateToUSFormat(value);
                    },
                },
                {
                    label: "End Date",
                    columnName: "endDate",
                    valueInterpreter: (value)=>{
                        return localDateToUSFormat(value);
                    },
                },
                canSeeRates ? {
                    label: "Rate",
                    columnName: "payout",
                    valueGenerator: (data)=>{
                        return  `$${data["payout"]} ${data.loadStatus === "Cancelled" ?"(TONU)" : ""}` ;
                    },
                    alignment: "center",
                } : null,
                {
                    label: "Status",
                    columnName: "loadStatus",
                    alignment: "right",
                    conditionalFormatting: (value)=>{
                        let color = loadStatusColorGenerator(value);
                        return `& > * {color:${color}; font-weight:bold;}`
        
                    },
                    valueInterpreter: (value)=>{
                        return loadStatusFormatter(value);
                    },
                }
            ].filter(Boolean);
            return {
                columnProportions: canSeeRates
                    ? "1.4fr 1fr 1fr 0.8fr 0.8fr"
                    : "1.4fr 1fr 1fr 0.8fr",
                columns,
            };
        }
        case "trucks":
            return {
                columnProportions: "1.4fr 1fr 0.6fr",
                columns: [
                    {
                        label: "Truck Number",
                        columnName: "truckNumber",
                        clickable: true,
                        identifierColumnName: "id",
                        path: '/trucks/',
                    },
                    {
                        label: "Current Trailer",
                        valueGenerator: (data)=>{
                            return NestedValueExtractor(data, [["currentTrailer", "trailerNumber"]]);
                        },
                        
                    },
                    {
                        label: "Status",
                        columnName: "active",
                        path: null,
                        conditionalFormatting: (value)=>{
                            if(value)return null;
                            return `& > * {color:white;
                                    font-weight:600;
                                    background:var(--alert-color);
                                    padding: 0.1rem 0.9rem;
                                    border-radius:1rem;}`
            
                        },
                        valueInterpreter: (value)=>{
                            if(value)return "Active";
                            return "Inactive"
                        },
                        alignment: "right",
                    },
                ],
            }
        case "trailers":
            return {
                columnProportions: "1fr 1.3fr 1fr 0.7fr",
                columns: [
                    {
                        label: "Trailer Number",
                        columnName: "trailerNumber",
                        clickable: true,
                        identifierColumnName: "id",
                        path: '/trailers/',
                    },
                    {
                        label: "Owned By",
                        columnName: "company",
                        valueGenerator: (data) => {
                            if(data["company"]){
                                return data["company"]["companyName"];
                            }else{
                                return "THIRD PARTY TRAILER"
                            }
                        },
                    },
                    {
                        label: "Current Truck",
                        valueGenerator: (data) => {
                            return NestedValueExtractor(data, "currentTruck");
                        },
                        alignment: "center",
                    },
                    {
                        label: "Status",
                        columnName: "trailerStatus",
                        conditionalFormatting: (value) => {
                            if(value === "Active")return ``;
                            let bgColor = "--alert-color";
                            if(value === "AtShop" || value === "Pending"){
                                bgColor = "--inProgressYellow";
                            }
                            return `& > * {color:var(${bgColor}); font-weight:900; }`;
                        },
                        valueInterpreter: (value) => {
                            if (value === "AtShop") return "At Shop";
                            if (value === "OutOfOrder") return "Out Of Order";
                            return value
                        },
                        alignment:"right",
                    },
                ],
            }
        case "drivers":
            return {
                columnProportions: "1fr 1.3fr 1fr 0.7fr",
                columns: [
                    {
                        label: "First Name",
                        columnName: "firstName",
                        identifierColumnName: "id",
                        clickable: true,
                        path: "/drivers/",
                    },
                    {
                        label: "Last Name",
                        columnName: "lastName",
                    },
                    {
                        label: "Driver Type",
                        columnName: "driverType",
                        valueInterpreter: driverTypeFormatter,
                        alignment:"center",
                    },
                    {
                        label: "Driver Status",
                        columnName: "driverStatus",
                        valueInterpreter: DriverStatusFormatter,
                        conditionalFormatting: (value)=>{
                            if(value !== "inactive")return null;
                            return `& > * {color:var(--alert-color);
                                    font-weight:700;`
            
                        },
                        alignment:"right",
                    },
                ]
            }
        case "brokers":
            return BROKER_LISTING_CONF;
        default:
            return;
    }
}
export const SearchResultsView = ({data, canSeeRates = true}) => {
    const keys = Object.keys(data);
    return <>
        {
            keys.map((key, index) =>{
                return <div key={index}>
                    <h2>{capitalizeFirstLetter(key)}</h2>
                    <hr />
                    <ListView ListConf={lblToConf(key, canSeeRates)}
                        Data={data[key]}
                        Error={null}
                        NoFooter={true}
                    />
                </div>
            })
        }
    </>
}